import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
// using CSS grid via Box for simple 3-column layout
import Button from "@mui/material/Button";
import PeopleIcon from "@mui/icons-material/People";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Kbd } from "./Kbd";
import { useShiftKey } from "../hooks/useShiftKey";
import { JUMP_ACTIONS } from "../hooks/useJumpShortcuts";

type Props = {
  onClose: () => void;
  filterText?: string;
};

// Icons are presentation-only and live with the component; everything else
// (path, shortcut, label key) comes from the shared JUMP_ACTIONS definition.
const icons: Record<string, React.ReactNode> = {
  patients: <PeopleIcon fontSize="large" />,
  appointments: <CalendarMonthIcon fontSize="large" />,
  new_appointment: <EventAvailableIcon fontSize="large" />,
};

export const JumpMenuActions: React.FC<Props> = ({
  onClose,
  filterText = "",
}) => {
  const { t } = useTranslation();
  const shiftHeld = useShiftKey();

  const routeItems = JUMP_ACTIONS.map((action) => ({
    key: action.key,
    path: action.path,
    icon: icons[action.key],
    label: t(`jumpMenu.${action.key}`, { defaultValue: action.defaultValue }),
    shortcut: action.code.replace("Digit", ""),
  }));

  const filteredItems = routeItems.filter((item) =>
    item.label.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}
    >
      {filteredItems.map((item) => (
        <Box key={item.key}>
          <Button
            fullWidth
            component={Link}
            to={item.path}
            onClick={() => {
              onClose();
            }}
            variant="outlined"
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 0,
              minHeight: 108,
              textTransform: "none",
              textAlign: "center",
            }}
          >
            {shiftHeld && item.shortcut && (
              <Box sx={{ position: "absolute", top: 6, right: 6 }}>
                <Kbd aria-hidden="true">{item.shortcut}</Kbd>
              </Box>
            )}
            {item.icon}
            <Typography variant="body2" sx={{ mt: 1 }}>
              {item.label}
            </Typography>
          </Button>
        </Box>
      ))}
    </Box>
  );
};

export default JumpMenuActions;
